import * as React from 'react';
import { CheckCircle2, Info, AlertTriangle, AlertCircle } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * NotificationCard — inline alert. Spec: handoff/COMPONENT_SPECS.md.
 * Tone-coloured 3px left border + tinted bg. Title + body + optional timestamp.
 */

export type NotificationTone = 'info' | 'success' | 'warning' | 'danger';

const TONES: Record<
  NotificationTone,
  { border: string; bg: string; icon: React.ComponentType<{ className?: string }>; iconColor: string }
> = {
  info: { border: 'border-l-trust', bg: 'bg-trust/5', icon: Info, iconColor: 'text-trust' },
  success: { border: 'border-l-success', bg: 'bg-success/5', icon: CheckCircle2, iconColor: 'text-success' },
  warning: { border: 'border-l-warning', bg: 'bg-warning/5', icon: AlertTriangle, iconColor: 'text-warning' },
  danger: { border: 'border-l-danger', bg: 'bg-danger/5', icon: AlertCircle, iconColor: 'text-danger' },
};

interface NotificationCardProps {
  tone: NotificationTone;
  title: React.ReactNode;
  body?: React.ReactNode;
  timestamp?: React.ReactNode;
  className?: string;
}

function NotificationCard({ tone, title, body, timestamp, className }: NotificationCardProps) {
  const t = TONES[tone];
  const Icon = t.icon;
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-3 rounded border-l-[3px] border border-border p-3',
        t.border,
        t.bg,
        className
      )}
    >
      <Icon className={cn('mt-0.5 size-4 shrink-0', t.iconColor)} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {body && <div className="mt-0.5 text-sm text-muted-foreground">{body}</div>}
      </div>
      {timestamp && (
        <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{timestamp}</span>
      )}
    </div>
  );
}

export { NotificationCard };
export type { NotificationCardProps };
